/**
 * The small round play control — the one that sits in front of a line of text: a
 * dialogue sentence, a phrase row, the phrase preview's headword.
 *
 * It is deliberately **the same button everywhere**: 28px, white, a hairline
 * `neutral-200` border and the accent triangle inside. That makes it the single
 * exception to the "no borders on a converted screen" rule — a filled circle read
 * as a second, competing accent next to every line, and the outline is what keeps
 * it quiet enough to repeat twenty times down a list.
 *
 * It had grown three near-copies (`topic.js`, `phraseRow.js`, `phrasePreview.js`)
 * that were already drifting — one had no hover, one a different top margin — so
 * the markup lives here and the call sites pass only what actually differs.
 *
 * Note the big round button in the bottom play bar is **not** this control: that
 * one is a filled brand FAB, the screen's primary action, and lives in
 * `playBar.js`.
 */

import { ICONS } from './icons.js';

/**
 * @param {object} options
 *   - hook: the caller's own attribute(s), verbatim — `data-play-sentence="42"`,
 *     `data-play-phrase="7"`, or a bare `data-play`. This is what each screen's
 *     click handler matches on.
 *   - label: `aria-label` in the idle state. A screen that swaps the icon for a
 *     pause glyph relabels the button itself.
 *   - hasAudio: `false` renders it disabled — no cached clip, nothing to play.
 *   - className: positioning only (the row's optical alignment), never colour.
 */
export function playButtonHtml({ hook = '', label = 'Odtwórz', hasAudio = false, className = '' }) {
  return `
    <button type="button" ${hook} ${hasAudio ? '' : 'disabled'} aria-label="${label}"
      class="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-neutral-200 bg-white text-brand transition hover:bg-lilac/40 active:scale-95 disabled:text-neutral-300 disabled:hover:bg-white ${className}">
      ${ICONS.play('h-4 w-4')}
    </button>`;
}
